import { combineReducers } from 'redux';
import { shouldFormBeReadOnly } from '../../utilities';
import { normalizeAPIError, getNormalizedErrorMessage } from '../../errors';
import formSettings from './constants';

const setIdFromActionType = (type, formId) => {
  switch (type) {
    case 'POST_LOGOUT_SUCCESS':
    case 'FETCH_ACTIVE_USER_FAILURE':
      return 'teacherLogin';
    default:
      return formId;
  }
};

export const setInitialState = formName => {
  const {
    autoFocusField,
    fieldNames,
    fieldGroups,
    onlySubmitChanges,
  } = formSettings[formName];
  return {
    isPosting: false,
    focusedField: autoFocusField,
    message: null,
    readOnly: false,
    isReconciling: false,
    isAwaitingUpdatedToken: false,
    onlySubmitChanges,
    fieldNames,
    fieldGroups,
  };
};

const byId = (state = {}, action) => {
  const { formId, formName, fieldName, error, type, isFocused } = action;
  const settings = formSettings[formName];
  if (!settings) return state;

  const autoFocusField = formName
    ? formSettings[formName].autoFocusField
    : null;

  // We’ll use this to update a single form at the end of the switch statement.
  const updatedFormId = setIdFromActionType(type, formId);
  let stateChanges = {};
  switch (action.type) {
    case 'INITIALIZE_FORM':
      return {
        ...state,
        [formId]: {
          ...setInitialState(formName),
          ...action.initialFormValues,
        },
      };
    case 'FIELD_FOCUS_CHANGE':
      stateChanges = {
        focusedField: isFocused ? fieldName : null,
      };
      break;
    case 'SUBMIT_FORM_REQUEST':
      stateChanges = {
        isPosting: true,
        message: null,
      };
      break;
    case 'SUBMIT_FORM_SUCCESS': {
      const animationType =
        formName === 'forgotPassword' ? 'static' : undefined;
      const successMessage =
        formName === 'createClass'
          ? null
          : {
              type: 'success',
              msgId: `form.${formName}.success_message`,
              animationType,
            };
      stateChanges = {
        ...setInitialState(formName),
        message: successMessage,
      };
      break;
    }
    case 'SUBMIT_FORM_FAILURE': {
      const readOnly = shouldFormBeReadOnly(error);
      const message = normalizeAPIError(error, type, formName);
      stateChanges = {
        isPosting: false,
        readOnly,
        message,
      };
      break;
    }
    case 'POST_LOGOUT_SUCCESS':
      stateChanges = {
        message: {
          type: 'success',
          msgId: 'logout.success_message',
        },
      };
      break;
    case 'FETCH_ACTIVE_USER_FAILURE':
      stateChanges = {
        message: getNormalizedErrorMessage('errors.expired_auth_token'),
      };
      break;
    case 'RESET_FORM':
      stateChanges = {
        isPosting: false,
        isReconciling: false,
        focusedField: autoFocusField,
        message: null,
      };
      break;
    case 'SET_FORM_READONLY_VALUE':
      stateChanges = {
        readOnly: action.readOnly,
      };
      break;
    case 'ENABLE_FORM_RECONCILIATION_MODE':
      stateChanges = {
        isReconciling: true,
      };
      break;
    case 'FORM_REQUIRES_UPDATED_TOKEN':
      stateChanges = {
        isAwaitingUpdatedToken: true,
      };
      break;
    case 'FORM_RESOLVE_UPDATED_TOKEN':
      stateChanges = {
        isAwaitingUpdatedToken: false,
      };
      break;
    case 'CLEAR_FORM_MESSAGE':
      stateChanges = {
        message: null,
      };
      break;
    case 'SET_FORM_MESSAGE':
      stateChanges = {
        message: action.message,
      };
      break;
    default:
      break;
  }

  if (updatedFormId) {
    return {
      ...state,
      [updatedFormId]: {
        ...state[updatedFormId],
        ...stateChanges,
      },
    };
  }
  return state;
};

const multipageForms = (state = {}, action) => {
  switch (action.type) {
    case 'INITIALIZE_MULTIPAGE_FORM':
      return {
        ...state,
        [action.id]: action.initialValues || {},
      };
    case 'CLEAR_MULTIPAGE_FORM':
      return {
        ...state,
        [action.id]: {},
      };
    case 'UPDATE_MULTIPAGE_FORM':
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ...action.values,
        },
      };
    default:
      return state;
  }
};

const forms = combineReducers({
  byId,
  multipageForms,
});

export default forms;

const getAttributeValue = (state, formId, key) => {
  return Object.keys(state.byId).includes(formId)
    ? state.byId[formId][key]
    : undefined;
};

export const isFormReadOnly = (state, formId) =>
  getAttributeValue(state, formId, 'readOnly');
export const isFormReconciling = (state, formId) =>
  getAttributeValue(state, formId, 'isReconciling');
export const isFormAwaitingUpdatedToken = (state, formId) =>
  getAttributeValue(state, formId, 'isAwaitingUpdatedToken');
export const getFocusedFormField = (state, formId) =>
  getAttributeValue(state, formId, 'focusedField');
export const isFormPosting = (state, formId) =>
  getAttributeValue(state, formId, 'isPosting');
export const getFormMessage = (state, formId) =>
  getAttributeValue(state, formId, 'message');
export const getForm = (state, formId) => state.byId[formId];
export const getMultipageFormValues = (state, id) => state.multipageForms[id];
